import React, { useState } from 'react';
import { formatMoney } from 'shared/src/utils/money/money-utils';
import { formatDate, toUTCDate } from 'shared/src/date-string-utils/date-utils';
import {
  calcIndividualBlockBudgetV2,
  calcTotalMediaBudget,
  getUnitName
} from 'shared/src/domain/line-item/line-item-utils';
import { ExecutionLineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';
import { StrategyTrpcResponse } from 'shared/src/trpc-types/strategy-trpc-types';
import { PacingChartV2 } from '@/features/line-items/pacing-chart-v2';
import { cumulativeSpend } from 'shared/src/domain/line-item/pacing-helpers';
import { diffInDays } from '@/metrics/calculate-metrics';
import { formatPacingType } from '@/components/tables/table-utils';
import {
  blockUnits,
  dailyBudget,
  dailyUnits,
  genColor
} from '@/features/line-items/line-item-form/utils/pacing-display';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarDays } from '@fortawesome/pro-light-svg-icons';
import { ToggleSwitch } from '@/components/forms/ui/toggle-switch';

export type PacingScheduleProps = {
  strategy: StrategyTrpcResponse;
  lineItem: ExecutionLineItemTrpcResponse;
};

export function PacingSchedule({ strategy, lineItem }: PacingScheduleProps) {
  if (
    !lineItem.pacing_details ||
    !lineItem.dates.from ||
    !lineItem.dates.to ||
    lineItem.price == null
  )
    return null;

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <PacingScheduleHeader lineItem={lineItem} strategy={strategy} />
      <PacingScheduleChart lineItem={lineItem} strategy={strategy} />
      <div className="-mt-4 px-1 pb-8">
        <p className="mb-2 text-center text-sm text-gray-500">
          Pacing block <span className="font-semibold">dates</span>,{' '}
          <span className="font-semibold">budget</span>, and{' '}
          <span className="font-semibold">units</span>.
        </p>
        {lineItem.pacing_details.map((block, idx) => (
          <PacingBlockRow
            key={idx}
            block={block}
            index={idx}
            lineItem={lineItem}
            strategy={strategy}
          />
        ))}
      </div>
    </div>
  );
}

type PacingScheduleHeaderProps = {
  lineItem: ExecutionLineItemTrpcResponse;
  strategy: StrategyTrpcResponse;
};

function PacingScheduleHeader({ lineItem, strategy }: PacingScheduleHeaderProps) {
  const days = diffInDays(toUTCDate(lineItem.dates.from!), toUTCDate(lineItem.dates.to!));

  // Always use planned_unit_cost for calculating units (how many we can buy)
  // planned_unit_cost is stored as per-unit in DB (e.g., $0.004185 for $4.185 CPM)
  // effective_unit_price is derived from selling price, not cost - wrong for unit calcs
  const unitCost = lineItem.planned_unit_cost;

  const totalMediaBudget = calcTotalMediaBudget(
    lineItem.pacing_details!,
    lineItem.line_item_type,
    lineItem.planned_margin,
    lineItem.referral_commission_rate,
    strategy.referral_commission_rate
  );
  // Calculate total units from total media budget to avoid rounding accumulation across blocks
  const totalUnits = unitCost && unitCost > 0 ? Math.round(totalMediaBudget / unitCost) : 0;

  return (
    <div className="flex flex-col gap-1 rounded-lg bg-secondary p-1">
      {/* Row 1: Pacing Type */}
      <div className="flex gap-1">
        <div className="flex flex-1 flex-col rounded-sm bg-white px-2 py-2">
          <span className="text-sm font-semibold">{formatPacingType(lineItem.pacing_type)}</span>
          <span className="text-xs font-light text-gray-500">Pacing Type</span>
        </div>
      </div>
      {/* Row 2: Start Date, End Date, Duration */}
      <div className="flex gap-1">
        <div className="flex flex-1 flex-col rounded-sm bg-white px-2 py-2">
          <div className="flex items-center gap-1">
            <FontAwesomeIcon className="text-gray-500" icon={faCalendarDays} size="sm" />
            <span className="text-sm font-semibold">{formatHeaderDate(lineItem.dates.from!)}</span>
          </div>
          <span className="text-xs font-light text-gray-500">Start date</span>
        </div>
        <div className="flex flex-1 flex-col rounded-sm bg-white px-2 py-2">
          <div className="flex items-center gap-1">
            <FontAwesomeIcon className="text-gray-500" icon={faCalendarDays} size="sm" />
            <span className="text-sm font-semibold">{formatHeaderDate(lineItem.dates.to!)}</span>
          </div>
          <span className="text-xs font-light text-gray-500">End date</span>
        </div>
        {days && (
          <div className="flex flex-col rounded-sm bg-white px-2 py-2">
            <span className="text-sm font-semibold">{days}</span>
            <span className="text-xs font-light text-gray-500">Duration</span>
          </div>
        )}
      </div>
      {/* Row 3: Media Budget, Total Units */}
      <div className="flex gap-1">
        <div className="flex flex-1 flex-col rounded-sm bg-white px-2 py-2">
          <span className="text-sm font-semibold">{formatMoney(totalMediaBudget)}</span>
          <span className="text-xs font-light text-gray-500">Media Budget</span>
        </div>
        <div className="flex flex-1 flex-col rounded-sm bg-white px-2 py-2">
          <span className="text-sm font-semibold">{totalUnits.toLocaleString()}</span>
          <span className="text-xs font-light text-gray-500">
            Total {getUnitName(lineItem.unit_price_type_name || 'CPM').full}
          </span>
        </div>
      </div>
    </div>
  );
}

function formatHeaderDate(date: string): string {
  const month = formatDate(date, 'MMM');
  const day = formatDate(date, 'd');
  const year = formatDate(date, 'yy');
  return `${month} ${day} '${year}`;
}

type PacingScheduleChartProps = {
  lineItem: ExecutionLineItemTrpcResponse;
  strategy: StrategyTrpcResponse;
};

function PacingScheduleChart({ lineItem, strategy }: PacingScheduleChartProps) {
  const [isCumulative, setIsCumulative] = useState(true);

  const rate = lineItem.referral_commission_rate ?? strategy.referral_commission_rate ?? 0;
  const data = cumulativeSpend(lineItem.dates, lineItem.pacing_details!).map(day => {
    const priceValue = rate ? day.cumulativeTotal * (1 - rate) : day.cumulativeTotal;
    const mediaBudget = priceValue * (1 - (lineItem.planned_margin || 0));
    return { ...day, cumulativeTotal: mediaBudget };
  });

  return (
    <>
      <div className="flex justify-end px-1 py-2">
        <ToggleSwitch checked={isCumulative} onChange={setIsCumulative} label="Cumulative" />
      </div>
      <div className="min-h-[250px] shrink-0">
        <PacingChartV2 data={data} blocks={lineItem.pacing_details!} isCumulative={isCumulative} />
      </div>
    </>
  );
}

type PacingBlockRowProps = {
  block: { dates: { from: string; to: string }; budget: number };
  index: number;
  lineItem: ExecutionLineItemTrpcResponse;
  strategy: StrategyTrpcResponse;
};

export function PacingBlockRow({ block, index, lineItem, strategy }: PacingBlockRowProps) {
  const isStandardType = lineItem.line_item_type === 'standard';
  const mediaBudget = isStandardType
    ? calcIndividualBlockBudgetV2(
        block,
        lineItem.planned_margin,
        lineItem.referral_commission_rate,
        strategy.referral_commission_rate
      )
    : block.budget;

  // Always use planned_unit_cost for calculating units (how many we can buy)
  // planned_unit_cost is stored as per-unit in DB (e.g., $0.004185 for $4.185 CPM)
  const unitCost = lineItem.planned_unit_cost ?? undefined;
  const daysInBlock = diffInDays(toUTCDate(block.dates.from), toUTCDate(block.dates.to));
  const unitLabel = getUnitName(lineItem.unit_price_type_name || 'CPM').abbr;

  const unitsValue = blockUnits(mediaBudget, unitCost);
  const dailyUnitsValue = dailyUnits({ dates: block.dates, budget: mediaBudget }, unitCost);
  const dailyBudgetValue = dailyBudget(mediaBudget, block.dates);

  const { startPercent, endPercent } = calculateBlockProgress(lineItem.pacing_details, index);
  const displayUnitLabel = unitLabel.charAt(0).toUpperCase() + unitLabel.slice(1).replace('.', '');

  return (
    <div className="flex gap-2 rounded-lg p-2">
      <div className={`w-1 rounded-full ${genColor(index)}`} />
      <div className="flex flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-800">Block {index + 1}</span>
            <span className="text-sm text-gray-800">
              {formatBlockDateRange(block.dates.from, block.dates.to)}
            </span>
          </div>
          {daysInBlock && (
            <DaysProgressIndicator
              days={daysInBlock}
              startPercent={startPercent}
              endPercent={endPercent}
            />
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="flex gap-2">
            <div className="rounded bg-white px-2 py-1 text-xs shadow-sm">
              {formatMoney(mediaBudget)} <span className="text-gray-500">$</span>
            </div>
            {dailyBudgetValue && (
              <div className="rounded bg-white px-2 py-1 text-xs shadow-sm">
                {dailyBudgetValue} <span className="text-gray-500">$/day</span>
              </div>
            )}
          </div>
          {unitsValue && (
            <div className="flex gap-2">
              <div className="rounded bg-white px-2 py-1 text-xs shadow-sm">
                {unitsValue} <span className="text-gray-500">{displayUnitLabel}</span>
              </div>
              {dailyUnitsValue && (
                <div className="rounded bg-white px-2 py-1 text-xs shadow-sm">
                  {dailyUnitsValue} <span className="text-gray-500">{displayUnitLabel}/day</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type DaysProgressIndicatorProps = {
  days: number;
  startPercent?: number;
  endPercent?: number;
  colorClass?: string;
  label?: string;
  showDurationCoverageBar?: boolean;
};

export function DaysProgressIndicator({
  days,
  startPercent = 0,
  endPercent = 100,
  colorClass = 'bg-gradient-to-r from-blue-400 to-blue-600',
  label = 'Days',
  showDurationCoverageBar = true
}: DaysProgressIndicatorProps) {
  return (
    <div className="flex h-6 flex-col justify-center rounded bg-white px-2 shadow-sm">
      <div className="text-xs">
        {days} <span className="text-gray-500">{label}</span>
      </div>
      {showDurationCoverageBar && (
        <div className="relative h-1 w-full overflow-hidden rounded-full bg-gray-200">
          <div
            className={`absolute top-0 h-full rounded-full ${colorClass}`}
            style={{ left: `${startPercent}%`, width: `${endPercent - startPercent}%` }}
          />
        </div>
      )}
    </div>
  );
}

function formatBlockDateRange(from: string, to: string): string {
  const fromMonth = formatDate(from, 'MMM');
  const toMonth = formatDate(to, 'MMM');
  const fromYear = formatDate(from, 'yy');
  const toYear = formatDate(to, 'yy');
  const fromDay = formatDate(from, 'd');
  const toDay = formatDate(to, 'd');

  if (fromMonth === toMonth && fromYear === toYear) {
    return `${fromMonth} ${fromDay} - ${toDay} '${fromYear}`;
  }
  if (fromYear === toYear) {
    return `${fromMonth} ${fromDay} - ${toMonth} ${toDay} '${fromYear}`;
  }
  return `${fromMonth} ${fromDay} '${fromYear} - ${toMonth} ${toDay} '${toYear}`;
}

function calculateBlockProgress(
  pacingDetails: { dates: { from: string; to: string } }[] | null | undefined,
  blockIndex: number
): { startPercent: number; endPercent: number } {
  if (!pacingDetails || pacingDetails.length === 0) {
    return { startPercent: 0, endPercent: 100 };
  }

  const blockDays = pacingDetails.map(block =>
    diffInDays(toUTCDate(block.dates.from), toUTCDate(block.dates.to))
  );

  const totalDays = blockDays.reduce((sum, days) => sum + (days || 0), 0);
  if (totalDays === 0) {
    return { startPercent: 0, endPercent: 100 };
  }

  const daysBefore = blockDays.slice(0, blockIndex).reduce((sum, days) => sum + (days || 0), 0);
  const thisBlockDays = blockDays[blockIndex] || 0;

  return {
    startPercent: (daysBefore / totalDays) * 100,
    endPercent: ((daysBefore + thisBlockDays) / totalDays) * 100
  };
}
